import { FC, memo } from 'react';
import { FieldValues, SubmitHandler, useFormContext } from 'react-hook-form';
import {
  Button,
  TextTitle,
  TextBody,
  TextCaption,
  Spacer,
  Stack,
  Card,
  Icon,
  IconButton,
  Textarea,
  Tooltip,
  Input,
} from '@pypestream/design-system';

import { useKnowledgeAgentUpload } from '../../../hooks';
import { useBobcatFlag } from '../../../hooks/use-bobcat-flag';

import './step-agent.scss';

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface StepAgentProps {
  nextStep: SubmitHandler<FieldValues>;
  backStep?: () => void;
  uploadedFiles: UploadedFile[];
  addFile: (file: File) => void;
  removeFile: (fileId: string) => void;
  disabled?: boolean;
}

interface FileItemProps {
  file: UploadedFile;
  testId?: string;
  onRemove: (id: string) => void;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (
    fileName: string
  ) => 'file-pdf' | 'file-doc' | 'file-txt' | 'file-odt' | 'data';
}

const FileItem = memo<FileItemProps>(
  ({ testId, file, onRemove, formatFileSize, getFileIcon }: FileItemProps) => (
    <Card
      test-id={testId}
      key={file.id}
      size="small"
      shadow="none"
      className="c-create-solution-agent__file"
    >
      <Icon
        name={getFileIcon(file.name)}
        className="c-create-solution-agent__file-icon"
      />
      <TextBody>{file.name}</TextBody>
      <TextCaption>{formatFileSize(file.size)}</TextCaption>
      <IconButton
        size="small"
        name="delete"
        slot="hover-action"
        onClick={() => onRemove(file.id)}
      />
    </Card>
  )
);

FileItem.displayName = 'FileItem';

interface FileUploadSectionProps {
  uploadedFiles: UploadedFile[];
  addFile: (file: File) => void;
  removeFile: (fileId: string) => void;
}

export const FileUploadSection = memo<FileUploadSectionProps>(
  ({ uploadedFiles, addFile, removeFile }: FileUploadSectionProps) => {
    const { customFileUpload, formatFileSize, getFileIcon } =
      useKnowledgeAgentUpload({
        addFile,
      });

    return (
      <>
        {customFileUpload}
        {uploadedFiles?.length > 0 && (
          <>
            <Spacer size="xsmall" />
            <div className="c-create-solution-agent__files-list">
              <Stack gutter="xsmall">
                {uploadedFiles?.map((file) => (
                  <FileItem
                    testId={`create-solution-modal-file-item-${file.id}`}
                    key={file.id}
                    file={file}
                    onRemove={removeFile}
                    formatFileSize={formatFileSize}
                    getFileIcon={getFileIcon}
                  />
                ))}
              </Stack>
            </div>
          </>
        )}
      </>
    );
  }
);

FileUploadSection.displayName = 'FileUploadSection';

export const StepAgent: FC<StepAgentProps> = ({
  nextStep,
  backStep,
  uploadedFiles,
  addFile,
  removeFile,
  disabled = false,
}) => {
  const { register, handleSubmit, watch, setValue } = useFormContext();
  const { errors } = useFormContext().formState;

  const brandName = watch('brandName');
  const isBrandNameValid = Boolean(brandName?.trim());

  // Project Bobcat, gated: this step is the SHIPPED Create Solution wizard,
  // reached by every user, not a new Bobcat-only surface — so Skip and the
  // paste-content option change behavior on shipped UI (same reasoning as
  // `cellPropertyEditor`). Off by default, this step behaves exactly as it
  // did before: file upload only, brand name required to proceed.
  const knowledgeAgentIntakeEnabled =
    useBobcatFlag('knowledgeAgentIntake') === 'allowed';

  return (
    <>
      <TextTitle size="small" textAlign="center">
        Create a Knowledge Agent
      </TextTitle>
      <Spacer size="xlarge" />
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Brand Name</TextBody>
      </Stack>
      <Spacer size="2xsmall" />
      <Input
        test-id="create-solution-modal-brand-name-input"
        variant="outlined"
        placeholder="Brand Name"
        {...register('brandName', {
          required: 'Brand Name is required',
        })}
        onInput={(e) => {
          // @ts-expect-error - TODO: fix this
          const { value } = e.target;
          setValue('brandName', value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }}
      />
      {errors.brandName && (
        <>
          <Spacer size="xsmall" />
          <TextBody variant="secondary" size="small">
            {errors.brandName.message as string}
          </TextBody>
        </>
      )}
      <Spacer />
      <Stack alignItems="center" gutter="2xsmall">
        <TextBody size="small">Tone</TextBody>
      </Stack>
      <Spacer size="2xsmall" />
      <Textarea
        variant="outlined"
        placeholder="Tone"
        rows={2}
        {...register('tone')}
      ></Textarea>
      <Spacer />
      {knowledgeAgentIntakeEnabled && (
        <>
          <TextBody size="small">Paste content</TextBody>
          <Spacer size="2xsmall" />
          <Textarea
            variant="outlined"
            rows={7}
            {...register('knowledgeAgentContext')}
            placeholder="Paste text here to build the knowledge base from it..."
          />
          {errors.knowledgeAgentContext && (
            <>
              <Spacer size="xsmall" />
              <TextBody variant="secondary" size="small">
                {errors.knowledgeAgentContext.message as string}
              </TextBody>
            </>
          )}
          <Spacer size="medium" />
          <TextCaption variant="secondary" textAlign="center">
            — or upload one or more documents instead —
          </TextCaption>
          <Spacer size="medium" />
        </>
      )}
      <FileUploadSection
        uploadedFiles={uploadedFiles}
        addFile={addFile}
        removeFile={removeFile}
      />
      <Spacer size="xlarge" />
      <Stack justifyContent="space-between">
        <Button
          size="large"
          variant="ghost"
          onClick={backStep}
          disabled={disabled}
        >
          Back
        </Button>

        <Stack gutter="small" direction="row">
          {knowledgeAgentIntakeEnabled && (
            <Tooltip
              placement="top"
              content="Create this solution with a Supervisor Agent only — no Knowledge Agent or knowledge base."
            >
              <Button
                test-id="create-solution-step-agent-skip-btn"
                size="large"
                variant="ghost"
                onClick={() => {
                  // Discard whatever this step was mid-filling: skipping means
                  // no Knowledge Agent at all, so nothing it staged should
                  // carry forward if the user later navigates Back into it.
                  // This form instance is shared across the whole wizard (every
                  // step is mounted at once under one FormProvider), so it is
                  // not enough to pass blank values to nextStep — the fields
                  // themselves must be reset, or Back followed by Next would
                  // resubmit whatever was typed before Skip was clicked.
                  uploadedFiles.forEach((file) => removeFile(file.id));
                  setValue('brandName', '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                  setValue('tone', '', { shouldDirty: true });
                  setValue('knowledgeAgentContext', '', {
                    shouldDirty: true,
                  });
                  nextStep({
                    brandName: '',
                    tone: '',
                    knowledgeAgentContext: '',
                  });
                }}
                disabled={disabled}
              >
                Skip
              </Button>
            </Tooltip>
          )}
          <Button
            test-id="create-solution-step-agent-next-btn"
            size="large"
            variant="primary"
            onClick={handleSubmit(nextStep)}
            disabled={disabled || !isBrandNameValid}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
